import Grid from '@mui/material/Grid';
import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  ExactAge,
  Gender,
  EmailAddressType,
  PhoneNumberType,
  CombinedFamilyInfo,
  IAddress,
  Address,
} from '../GeneratedClient';
import { useDirectoryModel } from '../Model/DirectoryModel';
import { Warning as WarningIcon } from '@mui/icons-material';
import { ValidateDatePicker } from '../Generic/Forms/ValidateDatePicker';
import {
  useAdultFamilyRelationships,
  useEthnicities,
} from '../Model/ConfigurationModel';
import { useParams } from 'react-router-dom';
import { useBackdrop } from '../Hooks/useBackdrop';
import { subYears } from 'date-fns';
import { useVisibleFamilies } from '../Model/Data';
import { AddressFormFields } from './AddressEditor';
import { familyLastName } from './FamilyUtils';

interface AddAdultDrawerProps {
  onClose: (event: object | undefined, reason: string) => void;
}

function optional(arg: string) {
  return arg.length > 0 ? arg : null;
}

export function AddAdultDrawer({ onClose }: AddAdultDrawerProps) {
  const { familyId } = useParams<{ familyId: string }>();
  const visibleFamilies = useVisibleFamilies();
  const family = visibleFamilies.find(
    (x) => x.family?.id === familyId
  ) as CombinedFamilyInfo;

  const [fields, setFields] = useState({
    firstName: '',
    lastName: '',
    gender: null as Gender | null,
    dateOfBirth: null as Date | null,
    ethnicity: '',
    isInHousehold: true,
    relationshipToFamily: '',
    address: null as IAddress | null,
    phoneNumber: '',
    phoneType: PhoneNumberType.Mobile,
    emailAddress: '',
    emailType: EmailAddressType.Personal,
    notes: null as string | null,
    concerns: null as string | null,
  });
  const {
    firstName,
    lastName,
    gender,
    dateOfBirth,
    ethnicity,
    isInHousehold,
    relationshipToFamily,
    address,
    phoneNumber,
    phoneType,
    emailAddress,
    emailType,
    notes,
    concerns,
  } = fields;
  const directoryModel = useDirectoryModel();

  const relationshipTypes = useAdultFamilyRelationships();
  const ethnicities = useEthnicities();
  const [dobError, setDobError] = useState(false);

  const withBackdrop = useBackdrop();

  async function addAdult() {
    await withBackdrop(async () => {
      if (firstName.length <= 0 || lastName.length <= 0) {
        alert('First and last name are required. Try again.');
      } else if (relationshipToFamily === '') {
        //TODO: Actual validation!
        alert('Family relationship was not selected. Try again.');
      } else {
        const age = dateOfBirth == null ? null : new ExactAge();
        if (dateOfBirth != null) age!.dateOfBirth = dateOfBirth;
        await directoryModel.addAdult(
          family.family!.id!,
          firstName,
          lastName,
          gender,
          age,
          optional(ethnicity),
          isInHousehold,
          relationshipToFamily,
          address == null
            ? null
            : new Address({ ...address, id: crypto.randomUUID() }),
          optional(phoneNumber),
          phoneType,
          optional(emailAddress),
          emailType,
          notes == null ? undefined : notes,
          concerns == null ? undefined : concerns
        );
        //TODO: Error handling (start with a basic error dialog w/ request to share a screenshot, and App Insights logging)
        onClose({}, ``);
      }
    });
  }

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 600 },
            top: 45,
            height: 'calc(100% - 45px)',
            display: 'flex',
          },
        },
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 3 }}>
        <Typography id="add-adult-title" variant="h6" sx={{ mb: 2 }}>
          Add Adult to {familyLastName(family)}
        </Typography>
        <form noValidate autoComplete="off">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                id="first-name"
                label="First Name"
                fullWidth
                size="small"
                value={firstName}
                onChange={(e) =>
                  setFields({ ...fields, firstName: e.target.value })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                id="last-name"
                label="Last Name"
                fullWidth
                size="small"
                value={lastName}
                onChange={(e) =>
                  setFields({ ...fields, lastName: e.target.value })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl required fullWidth size="small">
                <InputLabel id="family-relationship-label">
                  Relationship to Family
                </InputLabel>
                <Select
                  labelId="family-relationship-label"
                  label="Relationship to Family"
                  id="family-relationship"
                  value={relationshipToFamily}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      relationshipToFamily: e.target.value as string,
                    })
                  }
                >
                  <MenuItem key="placeholder" value="" disabled>
                    Select a relationship type
                  </MenuItem>
                  {relationshipTypes.map((relationshipType) => (
                    <MenuItem key={relationshipType} value={relationshipType}>
                      {relationshipType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isInHousehold}
                      onChange={(e) =>
                        setFields({
                          ...fields,
                          isInHousehold: e.target.checked,
                        })
                      }
                      name="isInHousehold"
                      color="primary"
                      size="small"
                    />
                  }
                  label="In Household"
                />
              </FormGroup>
            </Grid>
            <Grid size={12}>
              <Divider />
            </Grid>
            <Grid size={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Gender:</FormLabel>
                <RadioGroup
                  aria-label="genderType"
                  name="genderType"
                  row
                  value={gender == null ? null : Gender[gender]}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      gender: Gender[e.target.value as keyof typeof Gender],
                    })
                  }
                >
                  <FormControlLabel
                    value={Gender[Gender.Male]}
                    control={<Radio size="small" />}
                    label="Male"
                  />
                  <FormControlLabel
                    value={Gender[Gender.Female]}
                    control={<Radio size="small" />}
                    label="Female"
                  />
                  <FormControlLabel
                    value={Gender[Gender.SeeNotes]}
                    control={<Radio size="small" />}
                    label="See Notes"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ValidateDatePicker
                label="Date of birth"
                value={dateOfBirth}
                onChange={(date) => setFields({ ...fields, dateOfBirth: date })}
                maxDate={subYears(new Date(), 18)}
                openTo="year"
                disableFuture
                onErrorChange={setDobError}
                textFieldProps={{
                  size: 'small',
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="ethnicity-label">Ethnicity</InputLabel>
                <Select
                  labelId="ethnicity-label"
                  label="Ethnicity"
                  id="ethnicity"
                  value={ethnicity}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      ethnicity: e.target.value as string,
                    })
                  }
                >
                  <MenuItem key="placeholder" value="" disabled>
                    Select an ethnicity
                  </MenuItem>
                  {ethnicities.map((ethnicity) => (
                    <MenuItem key={ethnicity} value={ethnicity}>
                      {ethnicity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}></Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="phone-number"
                label="Phone Number"
                fullWidth
                size="small"
                type="tel"
                value={phoneNumber}
                onChange={(e) =>
                  setFields({ ...fields, phoneNumber: e.target.value })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="phoneType"
                  name="phoneType"
                  row
                  value={PhoneNumberType[phoneType]}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      phoneType:
                        PhoneNumberType[
                          e.target.value as keyof typeof PhoneNumberType
                        ],
                    })
                  }
                >
                  <FormControlLabel
                    value={PhoneNumberType[PhoneNumberType.Mobile]}
                    control={<Radio size="small" />}
                    label="Mobile"
                  />
                  <FormControlLabel
                    value={PhoneNumberType[PhoneNumberType.Home]}
                    control={<Radio size="small" />}
                    label="Home"
                  />
                  <FormControlLabel
                    value={PhoneNumberType[PhoneNumberType.Work]}
                    control={<Radio size="small" />}
                    label="Work"
                  />
                  {/* <FormControlLabel value={PhoneNumberType[PhoneNumberType.Fax]} control={<Radio size="small" />} label="Fax" /> */}
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="email-address"
                label="Email Address"
                fullWidth
                size="small"
                type="email"
                value={emailAddress}
                onChange={(e) =>
                  setFields({ ...fields, emailAddress: e.target.value })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="emailType"
                  name="emailType"
                  row
                  value={EmailAddressType[emailType]}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      emailType:
                        EmailAddressType[
                          e.target.value as keyof typeof EmailAddressType
                        ],
                    })
                  }
                >
                  <FormControlLabel
                    value={EmailAddressType[EmailAddressType.Personal]}
                    control={<Radio size="small" />}
                    label="Personal"
                  />
                  <FormControlLabel
                    value={EmailAddressType[EmailAddressType.Work]}
                    control={<Radio size="small" />}
                    label="Work"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid size={12}></Grid>
            <AddressFormFields
              address={address}
              onEdit={(value) => setFields({ ...fields, address: value })}
            />
            <Grid size={12}></Grid>
            <Grid size={12}>
              <TextField
                id="concerns"
                label="Concerns"
                placeholder="Note any safety risks, allergies, etc."
                multiline
                fullWidth
                variant="outlined"
                minRows={2}
                maxRows={5}
                size="small"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <WarningIcon />
                      </InputAdornment>
                    ),
                  },
                }}
                value={concerns == null ? '' : concerns}
                onChange={(e) =>
                  setFields({ ...fields, concerns: e.target.value })
                }
              />
            </Grid>
            <Grid size={12}>
              <TextField
                id="notes"
                label="Notes"
                placeholder="Space for any general notes"
                multiline
                fullWidth
                variant="outlined"
                minRows={2}
                maxRows={5}
                size="small"
                value={notes == null ? '' : notes}
                onChange={(e) =>
                  setFields({ ...fields, notes: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </form>
      </Box>
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          p: 2,
          pb: 'env(safe-area-inset-bottom)',
          backgroundColor: 'background.paper',
        }}
      >
        <Button onClick={() => onClose(undefined, 'cancel')} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={addAdult}
          disabled={dobError}
          variant="contained"
          color="primary"
        >
          Add to Family
        </Button>
      </Box>
    </Drawer>
  );
}

export { AddAdultDrawer as AddAdultDialog };
